import React, { useEffect, useRef } from 'react';

const WaveformOrbMic = ({ state = 'idle' }) => {
    const canvasRef = useRef(null);
    const audioRef = useRef({ stream: null, analyser: null, dataArray: null });

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const w = canvas.width;
        const h = canvas.height;
        const cx = w / 2;
        const cy = h / 2;
        let frame = 0;
        let animationFrameId = null;

        let audioCtx;
        let analyser;
        let source;
        let dataArray;

        const initMic = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                analyser = audioCtx.createAnalyser();
                source = audioCtx.createMediaStreamSource(stream);
                source.connect(analyser);
                analyser.fftSize = 256;
                const bufferLength = analyser.frequencyBinCount;
                dataArray = new Uint8Array(bufferLength);
                audioRef.current = { stream, analyser, dataArray };

                animate();
            } catch (err) {
                console.error('Mic access error:', err);
            }
        };

        const animate = () => {
            animationFrameId = requestAnimationFrame(animate);
            const { analyser, dataArray } = audioRef.current;

            ctx.clearRect(0, 0, w, h);
            frame += 0.05;

            const isActive = ['listening', 'speaking', 'thinking'].includes(state);
            const baseRadius = 80 + (isActive ? Math.sin(frame * 2) * 5 : 0);
            const amp = isActive && analyser
                ? (() => {
                    analyser.getByteTimeDomainData(dataArray);
                    const avg = dataArray.reduce((sum, v) => sum + Math.abs(v - 128), 0) / dataArray.length;
                    return Math.min(avg * 0.7, 30);
                })()
                : 0;

            // Glow ring
            ctx.beginPath();
            ctx.arc(cx, cy, baseRadius, 0, 2 * Math.PI);
            ctx.strokeStyle = getColor(state);
            ctx.lineWidth = 4;
            ctx.shadowBlur = isActive ? 20 : 5;
            ctx.shadowColor = getColor(state);
            ctx.stroke();

            // Inner waveform
            if (isActive) {
                drawWaveformCircle(ctx, cx, cy, baseRadius - 20, amp, 6, frame, state);
            }
        };

        const getColor = (state) => {
            switch (state) {
                case 'listening': return '#00ffff';
                case 'speaking': return '#00ff00';
                case 'thinking': return '#ffd700';
                default: return '#444'; // idle gray
            }
        };

        const drawWaveformCircle = (ctx, cx, cy, radius, amplitude, frequency, frame, state) => {
            const segments = 128;
            ctx.beginPath();
            for (let i = 0; i <= segments; i++) {
                const angle = (i / segments) * 2 * Math.PI;
                const mod = Math.sin(angle * frequency + frame) * amplitude;
                const r = radius + mod;
                const x = cx + r * Math.cos(angle);
                const y = cy + r * Math.sin(angle);
                if (i === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
            }
            ctx.closePath();
            ctx.lineWidth = 2;
            ctx.strokeStyle = getColor(state);
            ctx.shadowBlur = 0;
            ctx.stroke();
        };

        initMic();

        return () => {
            cancelAnimationFrame(animationFrameId);
            const { stream } = audioRef.current;
            if (stream) stream.getTracks().forEach(track => track.stop());
        };
    }, [state]);

    return (
        <canvas
            ref={canvasRef}
            width={300}
            height={300}
            style={{
                display: 'block',
                background: 'transparent',
                margin: '0 auto',
            }}
        />
    );
};

export default WaveformOrbMic;


